import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import * as React from 'react';
import type TranscriptTurnsListView from '@UI/Store/Interviews/Views/TranscriptTurnsListView';
import type TranscriptSpeakersListView from '@UI/Store/Interviews/Views/TranscriptSpeakersListView';
import TranscriptTurnRow from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/TranscriptTurnsReviewer/TranscriptTurnRow';
import { Button, confirmDialog, Skeleton } from '@verclam/ui-kit';
import { css } from '@emotion/react';
import useTurnRevision from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Providers/TurnRevisionProvider/useTurnRevision';
import { theme } from '@Guidelines/Theme/theme';
import type TranscriptTurnView from '@UI/Store/Interviews/Views/TranscriptTurnView';
import { setSpeakerAssignmentContext } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/Signals/speakerAssignmentSignal';
import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import useAudioPlayer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Providers/AudioPlayerProvider/useAudioPlayer';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import { useBeforeUnload } from 'react-router-dom';
import ErrorState from '@Guidelines/Component/ErrorState';
import EmptyState from '@Guidelines/Component/EmptyState';

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const footerStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	padding-top: 0.25rem;
`;

const footerMessageStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly turns: TranscriptTurnsListView | null;
	readonly turnsFetchError: Error | null;
	readonly refetchTurns: () => void;
	readonly speakers: TranscriptSpeakersListView | null;
	readonly participants: readonly InternalInterviewParticipantView[];
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly loadMoreTurns: () => void;
	readonly isLoadingMoreTurns: boolean;
	readonly hasNextPageError: boolean;
};

const TranscriptTurnsReviewerContainer: React.FC<Props> = ({
	turns,
	turnsFetchError,
	refetchTurns,
	speakers,
	participants,
	interviewId,
	subject,
	loadMoreTurns,
	isLoadingMoreTurns,
	hasNextPageError,
}) => {
	const { canModify: isWritable } = useSubjectModifiability(subject);

	const turnRevision = useTurnRevision();

	const audio = useAudioPlayer();

	const runGuarded = React.useCallback(
		(action: () => void): void => {
			if (!turnRevision.hasUnsavedEdit) {
				action();
				return;
			}

			confirmDialog({
				header: 'Correction en cours',
				message: "Une correction est en cours et n'a pas été enregistrée. La perdre ?",
				acceptLabel: 'Continuer sans enregistrer',
				acceptSeverity: 'danger',
				accept: () => {
					turnRevision.cancel();
					action();
				},
			});
		},
		[turnRevision],
	);

	const handleBeforeUnload = React.useCallback(
		(event: BeforeUnloadEvent): void => {
			if (!turnRevision.hasUnsavedEdit) {
				return;
			}

			event.preventDefault();
			event.returnValue = '';
		},
		[turnRevision.hasUnsavedEdit],
	);

	// The router's own hook: it carries its own listener cleanup, and works with the `<BrowserRouter>`
	// this application mounts — `useBlocker` does not (D-9).
	useBeforeUnload(handleBeforeUnload);

	const handleRequestEdit = React.useCallback(
		(turn: TranscriptTurnView): void => {
			if (turn.id === turnRevision.editingTurnId) {
				return;
			}

			runGuarded(() => turnRevision.openTurn(turn));
		},
		[runGuarded, turnRevision],
	);

	const handleRequestAssignSpeaker = React.useCallback(
		(turn: TranscriptTurnView): void => {
			if (turn.speakerId === null) {
				return;
			}

			const speakerId = turn.speakerId;

			runGuarded(() => {
				setSpeakerAssignmentContext({
					speakerId,
					subjectTypeKey: subject.typeKey,
					subjectId: subject.subjectId,
					interviewId,
					participants,
				});
			});
		},
		[runGuarded, subject, interviewId, participants],
	);

	const hasSpeakers = speakers !== null && speakers.list.length > 0;

	if (turns === null && turnsFetchError === null) {
		return (
			<div css={skeletonStackStyle}>
				<Skeleton height="4.5rem" animation="wave" shape="rectangle" />
				<Skeleton height="4.5rem" animation="wave" shape="rectangle" />
				<Skeleton height="4.5rem" animation="wave" shape="rectangle" />
			</div>
		);
	}

	if (turns === null) {
		return <ErrorState title="Impossible de charger le transcript" onRetry={refetchTurns} />;
	}

	if (turns.list.length === 0) {
		return <EmptyState title="Aucun tour de parole." />;
	}

	const remainingCount = turns.totalRecords - turns.list.length;

	return (
		<React.Fragment>
			<div css={listStyle}>
				{turns.list.map((turn) => (
					<TranscriptTurnRow
						key={turn.id}
						turn={turn}
						speaker={speakers?.findById(turn.speakerId) ?? null}
						hasSpeakers={hasSpeakers}
						isActive={audio.activeTurnId === turn.id}
						canSeek={turn.startSecond !== null && audio.isPositionable}
						onSeek={audio.seekToTurn}
						isEditing={turnRevision.editingTurnId === turn.id}
						formData={turnRevision.formData}
						isSubmitting={turnRevision.isSubmitting}
						onChangeText={turnRevision.changeText}
						onSubmit={turnRevision.submit}
						onCancel={turnRevision.cancel}
						onRequestEdit={handleRequestEdit}
						onRequestAssignSpeaker={handleRequestAssignSpeaker}
						isWritable={isWritable}
					/>
				))}
			</div>
			<div css={footerStyle}>
				{isLoadingMoreTurns ? (
					<Skeleton height="2.5rem" animation="wave" shape="rectangle" />
				) : hasNextPageError ? (
					<React.Fragment>
						<p css={footerMessageStyle}>La suite n'a pas pu être chargée.</p>
						<Button
							type="button"
							label="Réessayer"
							severity="secondary"
							outlined
							onClick={loadMoreTurns}
						/>
					</React.Fragment>
				) : audio.hasPlaybackExceededLoadedTurns && turns.hasMore ? (
					<React.Fragment>
						<p css={footerMessageStyle}>La lecture a dépassé les tours affichés.</p>
						<Button
							type="button"
							label="Afficher la suite"
							severity="secondary"
							outlined
							onClick={loadMoreTurns}
						/>
					</React.Fragment>
				) : turns.hasMore ? (
					<Button
						type="button"
						label={`Afficher la suite — ${remainingCount} tours restants`}
						severity="secondary"
						outlined
						onClick={loadMoreTurns}
					/>
				) : null}
			</div>
		</React.Fragment>
	);
};

export default TranscriptTurnsReviewerContainer;
